/*
this task responsible for:
- creating images for visual task and input reactions.
- creating auditory cases and input reactions.
*/

import { MovingTargetTask, TimelineTask } from './MovingTargetTask';
import { ATaskConfiguration, ITaskConfiguration } from './TaskDefinitions';
import { SubEvent } from 'sub-events';
import { AuditoryReactions } from '../../../enums/AuditoryReaction';
import CONSTANTS from '../../../CONSTANTS/CONSTANTS';
import Phaser from 'phaser';
import TaskScene from '../scene/TaskScene';

export class ReactionTaskConfiguration extends ATaskConfiguration {
  visual: boolean = false;
  auditory: boolean = false;
  ResetFrames: boolean = false;
  animation: boolean = false;

  timeBetweenAuditoryReaction: number = CONSTANTS.reaction.timeBetweenAuditoryReaction;
  timeToDecreaseAuditoryReaction: number = CONSTANTS.reaction.timeToDecreaseAuditoryReaction;

  timeBetweenAnimation: number = CONSTANTS.reaction.timeBetweenAnimation;
  timeOffsetAnimation: number = CONSTANTS.reaction.timeOffsetAnimation;

  timeBetweenVisualReaction: number = CONSTANTS.reaction.timeBetweenVisualReaction;
  timeToDecreaseVisualReaction: number = CONSTANTS.reaction.timeToDecreaseVisualReaction;

  audioReactions: Map<AuditoryReactions, string> = new Map<AuditoryReactions, string>([
    [AuditoryReactions.Single, 'auditorySingle'],
    [AuditoryReactions.Double, 'auditoryDouble'],
  ]);

  constructor(seed: number) {
    super(seed);
  }

  public Copy(): ReactionTaskConfiguration {
    let config = new ReactionTaskConfiguration(this.seed);
    config.visual = this.visual;
    config.auditory = this.auditory;
    config.timeToDecreaseAuditoryReaction = this.timeToDecreaseAuditoryReaction;
    config.timeBetweenAuditoryReaction = this.timeBetweenAuditoryReaction;
    config.timeToDecreaseVisualReaction = this.timeToDecreaseVisualReaction;
    config.timeBetweenVisualReaction = this.timeBetweenVisualReaction;
    config.timeBetweenAnimation = this.timeBetweenAnimation;
    config.timeOffsetAnimation = this.timeOffsetAnimation;
    config.enabled = this.enabled;
    config.ResetFrames = this.ResetFrames;
    config.animation = this.animation;
    return config;
  }

  ApplyDifficulty(difficultyConfig: ITaskConfiguration): void {
    let difConfig = difficultyConfig as ReactionTaskConfiguration;
    this.timeToDecreaseAuditoryReaction = difConfig.timeToDecreaseAuditoryReaction;
    this.timeBetweenAuditoryReaction = difConfig.timeBetweenAuditoryReaction;
    this.timeToDecreaseVisualReaction = difConfig.timeToDecreaseVisualReaction;
    this.timeBetweenVisualReaction = difConfig.timeBetweenVisualReaction;
    this.timeBetweenAnimation = difConfig.timeBetweenAnimation;
    this.timeOffsetAnimation = difConfig.timeOffsetAnimation;
  }
}

export class ReactionInputEvent {
  public readonly input: boolean;
  public readonly Task: ReactionTask;

  constructor(isDouble: boolean, task: ReactionTask) {
    this.input = isDouble;
    this.Task = task;
  }
}

export class ShapeReactionInputEvent {
  public readonly input: boolean;
  public readonly Task: ReactionTask;

  constructor(isDouble: boolean, task: ReactionTask) {
    this.input = isDouble;
    this.Task = task;
  }
}

export class ReactionTask extends TimelineTask<ReactionTaskConfiguration> {
  public static readonly REACTION_TIMELINE = 'reaction';
  public static readonly AUDITORY_TIMELINE = 'auditory';
  public static readonly ANIMATION_TIMELINE = 'animation';

  reactionButton?: Phaser.GameObjects.Text;
  movingTarget: MovingTargetTask;
  image?: Phaser.GameObjects.Sprite;
  timeline?: Phaser.Tweens.Timeline;
  postUpdateFunction: Function;
  auditoryReactions: Map<AuditoryReactions, Phaser.Sound.BaseSound> = new Map();
  runOutOfTime: boolean = false;
  animation?: Phaser.GameObjects.Sprite;
  // keyPressesDetected: number | undefined = 0;
  selectedImage: undefined | number = undefined;
  animationKeyPressesDetected: number = 0;
  animationTimelineActive: boolean = true;
  audioKeyPressesDetected: number = 0;
  audioTimelineActive: boolean = false;
  visualKeyPressesDetected: number = 0;
  visualTimelineActive: boolean = false;
  readonly RightOrWrongVisualExpected: SubEvent<ReactionInputEvent> = new SubEvent();
  readonly OnShapeChangeExpected: SubEvent<ShapeReactionInputEvent> = new SubEvent();

  public handleVisualButtonPress(): void {
    if (this.visualTimelineActive) this.visualKeyPressesDetected++;
  }
  public handleAuditoryButtonPress(): void {
    if (this.audioTimelineActive) {
      this.audioKeyPressesDetected++;
    }
  }
  public handleAnimationButtonPress(): void {
    if (this.animationTimelineActive) this.animationKeyPressesDetected++;
  }
  // birds: Phaser.GameObjects.Sprite;

  readonly OnInputExpected: SubEvent<ReactionInputEvent> = new SubEvent();

  readonly OnInputReceived: SubEvent<ReactionInputEvent> = new SubEvent();

  constructor(
    movingTarget: MovingTargetTask,
    configuration: ReactionTaskConfiguration,
    scene: Phaser.Scene
    // birds: Phaser.GameObjects.Sprite
  ) {
    super(configuration, scene, 'ReactionTask');
    // this.birds = birds;
    this.movingTarget = movingTarget;
    this.postUpdateFunction = () => {};
  }

  Create(configuration: ReactionTaskConfiguration, scene: Phaser.Scene): void {
    this.runOutOfTime = false;

    this.OnUpdate(configuration, this.configuration, scene, false);
    // Documentation on this is wrong from phaser?!?
    this.postUpdateFunction = (time: Number, delta: Number) => this.SynchronizeTrackerPosition(scene.sys);
    configuration.audioReactions.forEach((value, key) => {
      let sound = scene.sound.get(value);
      if (sound === null) {
        sound = scene.sound.add(value);
      }
      this.auditoryReactions.set(key, sound);
    });
  }

  public RebuildAfterResize(scene: Phaser.Scene) {
    const { width, height } = this.GetCurrenScreenSize(scene);

    if (this.image) {
      this.image.setScale(this.CalculateScalingFactor(width, height));
      this.SynchronizeTrackerPosition(scene.sys);
    }
  }

  Start(scene: Phaser.Scene): void {
    super.Start(scene);
    this.visualKeyPressesDetected = 0;
    scene.events.on('postupdate', this.postUpdateFunction);
  }

  Stop(scene: Phaser.Scene): void {
    super.Stop(scene);
    scene.events.removeListener('postupdate', this.postUpdateFunction);

    // TODO: Check if the timeline is not secretly getting an update after this?
    this.CleanupInteractionButton();
    this.visualKeyPressesDetected = 0;
    this.CleanupAnimation(scene);
  }

  OnUpdate(
    oldConfiguration: ReactionTaskConfiguration,
    configuration: ReactionTaskConfiguration,
    scene: Phaser.Scene,
    firstCreateCall: boolean
  ): void {
    this.CleanupInteractionButton();
    this.CleanupAnimation(scene);
    this.SetVisible(false);

    // If we change the configuration settings, we need to clear the keys tracked
    // TODO: is this an issue - do we sometimes call update without a config change?
    // it breaks visual task.
    // this.keyPressesDetected = undefined;

    // TODO: Implement this as a configuration & command & ui - in EMDR-Remote 2.0 with new designs.
    // let useReactionImages = true;

    // TODO: on the configuration, add in a type to see what kind of visual reaction we want to do.
    // EITHER: the classic (green/red) or the animation.
    // You can use this configuration then to test your animation implementation, but we'll implement the UI and command handling once Ana's design is done.
    let reaction = configuration.visual ? this.CreateVisual(configuration, scene) : scene.tweens.createTimeline();
    this.ReplaceTimeline(ReactionTask.REACTION_TIMELINE, reaction);

    let ts = scene as TaskScene;

    let auditory = configuration.auditory
      ? this.CreateAuditoryReaction(configuration, scene)
      : scene.tweens.createTimeline();
    this.ReplaceTimeline(ReactionTask.AUDITORY_TIMELINE, auditory);

    let animation = configuration.animation
      ? this.CreateVisualAnimation(configuration, scene)
      : scene.tweens.createTimeline();
    this.ReplaceTimeline(ReactionTask.ANIMATION_TIMELINE, animation);
  }

  CleanupAnimation(scene: Phaser.Scene) {
    this.movingTarget.image?.anims.stop();
    this.movingTarget.image?.setFrame(0);

    this.image?.setVisible(false);
    this.image?.destroy();
    this.image = undefined;
  }

  GetCurrenScreenSize(scene: Phaser.Scene) {
    const width: number = scene.game.canvas.width;
    let height: number = width * CONSTANTS.movingTarget.heightPercent; // Maintain aspect ratio (height = 60% of width)

    height = Math.min(height, scene.game.canvas.height); // Ensure height does not exceed the actual canvas height

    return { width, height };
  }

  // Ensures that the reaction button is not visible, and no-longer listening
  CleanupInteractionButton() {
    this.audioKeyPressesDetected = 0;
    this.animationKeyPressesDetected = 0;
    this.visualKeyPressesDetected = 0;
    this.visualTimelineActive = false;
    this.audioTimelineActive = false;
    this.animationTimelineActive = false;
  }

  CreateAuditoryReaction(configuration: ReactionTaskConfiguration, scene: Phaser.Scene): Phaser.Tweens.Timeline {
    let timeline = scene.tweens.createTimeline();
    let isKeyDownPreviousFrame = false;
    let ts = scene as TaskScene;

    let follower = { t: 0, vec: new Phaser.Math.Vector2() };
    let switchReactionWithNoReaction = true;
    let pickedReaction: AuditoryReactions | undefined = undefined;

    if (!!ts.calledBy) {
      ts.keyDown = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT);
    }
    const isLeftShiftDown = ts.keyDown?.isDown && ts.keyDown.location === 1; // 1 == left
    timeline.add({
      targets: follower,
      t: { from: 0, to: 1 },
      duration: switchReactionWithNoReaction
        ? configuration.timeBetweenAuditoryReaction
        : CONSTANTS.reaction.shortenTimeBetweenAuditoryReaction,
      repeat: -1,
      onUpdate: tween => {
        if (!this.audioTimelineActive) {
          this.audioTimelineActive = true;
        }
        let newSpeed =
          this.configuration.timeBetweenAuditoryReaction - this.configuration.timeToDecreaseAuditoryReaction;
        tween.setTimeScale(this.configuration.timeBetweenAuditoryReaction / newSpeed);

        if (!ts.isTablet && ts.calledBy === 'client') {
          //checking if it's left shift pressed. for right shift we can use 2;
          const isLeftShiftDown: boolean = !!(ts.keyDown?.isDown && ts.keyDown?.location === 1);

          if (isLeftShiftDown && !isKeyDownPreviousFrame) {
            if (this.audioKeyPressesDetected !== undefined) {
              this.audioKeyPressesDetected++;
            }
          }
          isKeyDownPreviousFrame = isLeftShiftDown;
        }
      },
      onRepeat: () => {
        if (switchReactionWithNoReaction) {
          // Pick a new reaction for the next cycle and trigger the audio.
          let reaction = [AuditoryReactions.Single, AuditoryReactions.Double];
          pickedReaction = this.random.pickAudioReaction(reaction);
          let pickedAudio = this.auditoryReactions.get(pickedReaction);

          // Only play audio if called by client
          if (ts.calledBy === 'client') {
            pickedAudio?.play();
          }
        } else {
          // If we selected a reaction the previous repeat, check if we got the correct input and trigger the listener.
          if (pickedReaction !== undefined) {
            if (ts.calledBy === 'client' && this.audioKeyPressesDetected !== undefined) {
              switch (pickedReaction) {
                case AuditoryReactions.Single:
                  this.SingleAuditoryCase(this.audioKeyPressesDetected);
                  break;
                case AuditoryReactions.Double:
                  this.DoubleAuditoryCase(this.audioKeyPressesDetected);
              }
            }
          }
          // Reset the keypresses for the next reaction
          this.audioKeyPressesDetected = 0;
        }
        switchReactionWithNoReaction = !switchReactionWithNoReaction;
      },
    });

    return timeline;
  }

  CreateVisual(configuration: ReactionTaskConfiguration, scene: Phaser.Scene): Phaser.Tweens.Timeline {
    let timeline = scene.tweens.createTimeline();
    let follower = { t: 0, vec: new Phaser.Math.Vector2() };
    let duration = configuration.timeBetweenVisualReaction;
    let switchImageWithNoImage = true;
    let isKeyDownPreviousFrame = false;
    let ts = scene as TaskScene;

    if (!!ts.calledBy) {
      ts.keySpace = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE, false);
    }

    timeline.add({
      targets: follower,
      t: { from: 0, to: 1 },
      duration: duration,
      repeat: -1,
      onUpdate: tween => {
        if (!this.visualTimelineActive) {
          this.visualTimelineActive = true;
        }
        let newDuration =
          this.configuration.timeBetweenVisualReaction - this.configuration.timeToDecreaseVisualReaction;
        tween.setTimeScale(this.configuration.timeBetweenVisualReaction / newDuration);

        if (!ts.isTablet) {
          if (ts.calledBy === 'client') {
            let isKeyDown = ts.keySpace != undefined ? scene.input.keyboard.checkDown(ts.keySpace) : false;
            if (isKeyDown && !isKeyDownPreviousFrame) {
              if (this.visualKeyPressesDetected !== undefined) this.visualKeyPressesDetected++;
            }

            isKeyDownPreviousFrame = isKeyDown;
          }
        }
      },
      onRepeat: tween => {
        if (this.image) {
          this.image.destroy();
          this.image = undefined;
        }

        if (switchImageWithNoImage) {
          this.selectedImage = this.random.between(0, 3);
          if (this.visualKeyPressesDetected === undefined) return;

          switch (this.selectedImage) {
            case 0:
              this.NoResponseToVisualCase(configuration, scene);
              break;

            case 1:
              this.OneClickToVisualCase(configuration, scene);
              break;

            case 2:
              this.TwoClicksToVisualCase(configuration, scene);
          }

          this.reactionButton?.off('pointerover');

          this.visualKeyPressesDetected = 0;
        } else {
          this.handleInputCheck(this.selectedImage, ts.calledBy === 'client', this.visualKeyPressesDetected);
          this.image = undefined;
        }
        switchImageWithNoImage = !switchImageWithNoImage;
      },
    });

    return timeline;
  }

  private handleInputCheck(
    selectedImage: number | undefined,
    isClient: boolean,
    keyPressesDetected: number | undefined
  ) {
    if (this.visualKeyPressesDetected === undefined) return;
    if (selectedImage === undefined || !isClient || keyPressesDetected === undefined) return;
    switch (selectedImage) {
      case 0:
        this.NoResponseCase(keyPressesDetected);
        break;

      case 1:
        this.OneClickCase(keyPressesDetected);
        break;

      case 2:
        this.TwoClicksCase(keyPressesDetected);
    }
  }

  private getAnimationTextureKey(textureManager: Phaser.Textures.TextureManager): string {
    const defaultKey = 'animation';

    const spriteTextureKey = this.movingTarget.image?.texture?.key;
    if (spriteTextureKey && textureManager.exists(spriteTextureKey)) {
      const spriteTexture = textureManager.get(spriteTextureKey);
      if (spriteTexture && spriteTexture.frameTotal > 1) {
        return spriteTextureKey;
      }
    }

    const movingTargetConfig = this.movingTarget.configuration;
    const candidateKey = movingTargetConfig.theme?.spriteKeyPostfix;

    if (candidateKey && textureManager.exists(candidateKey)) {
      const texture = textureManager.get(candidateKey);
      if (texture && texture.frameTotal > 1) {
        return candidateKey;
      }
    }

    if (textureManager.exists(defaultKey)) {
      return defaultKey;
    }

    return candidateKey ?? defaultKey;
  }

  ResetFrames(configuration: ReactionTaskConfiguration, scene: Phaser.Scene, maxDuration: number) {
    //reset animation, create new duration for pause phase and creating pause timeline
    const minDuration = 500;

    this.animationTimelineActive = false;
    this.movingTarget.image?.setFrame(0);

    const chosenPause = this.random.animationPause(minDuration, maxDuration);
    let animationPause = this.CreateVisualAnimationPause(configuration, scene, chosenPause);
    this.ReplaceTimeline(ReactionTask.ANIMATION_TIMELINE, animationPause);
  }

  CreateVisualAnimation(configuration: ReactionTaskConfiguration, scene: Phaser.Scene): Phaser.Tweens.Timeline {
    const baseDuration = configuration.timeBetweenAnimation;
    let timeline = scene.tweens.createTimeline();
    let follower = { t: 0, vec: new Phaser.Math.Vector2() };

    //everything we need for inputevent
    let ts = scene as TaskScene;
    let isKeyDownPreviousFrame = false;
    ts.keyDelete = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT, false);

    timeline.add({
      targets: follower,
      t: 1,
      ease: 'Transition',
      duration: baseDuration,
      onStart: () => {
        // Ensure we have a clean state
        this.animationKeyPressesDetected = 0;
        this.animationTimelineActive = true;
        // creating annimation.
        const textureKey = this.getAnimationTextureKey(scene.textures);
        const config = {
          key: 'animation',
          frames: scene.anims.generateFrameNumbers(textureKey, { start: 0, end: 15 }),
          frameRate: 15,
          yoyo: true,
          repeat: -1,
        };
        this.movingTarget.image?.anims.create(config);

        this.movingTarget.image?.anims.load(config.key);
      },
      onUpdate: tween => {
        let clientPressedKey = ts.calledBy === 'client' && this.animationKeyPressesDetected !== 0;
        let weNeedToProcessServerResponse =
          this.onServerResponseSignal !== undefined && this.onServerResponseSignal !== false;

        // [ 0 -> 1] => [ 0 .. 1 .. 1. 0 ]
        let localProgress = tween.progress < 0.5 ? tween.progress * 2 : 1 - (tween.progress - 0.5) * 2;
        const animationState = this.movingTarget.image?.anims;
        if (!animationState) {
          return;
        }

        if (!animationState.currentAnim) {
          // Attempt to (re)start the animation if it was cleared because of a theme swap
          const animationKey = 'animation';
          if (scene.anims.exists(animationKey)) {
            animationState.play(animationKey);
          } else {
            return;
          }
        }

        const currentAnimation = animationState.currentAnim;
        if (!currentAnimation) {
          return;
        }

        const frame = currentAnimation.getFrameByProgress(localProgress);
        if (frame) {
          animationState.setCurrentFrame(frame);
        }

        if (clientPressedKey || weNeedToProcessServerResponse) {
          if (ts.calledBy === 'client') {
            if (this.animationKeyPressesDetected > 0) {
              this.OnShapeChangeExpected.emit(new ShapeReactionInputEvent(true, this));
            } else {
              this.OnShapeChangeExpected.emit(new ShapeReactionInputEvent(false, this));
            }
          }
          this.ResetFrames(configuration, scene, baseDuration);
        }

        if (!ts.isTablet) {
          if (ts.calledBy === 'client') {
            let isKeyDown = ts.keyDelete != undefined ? scene.input.keyboard.checkDown(ts.keyDelete) : false;

            if (isKeyDown && !isKeyDownPreviousFrame) {
              this.animationKeyPressesDetected++;
            }
            isKeyDownPreviousFrame = isKeyDown;
          }
        }
      },
      onComplete: () => {
        // If a client has not pressed anything, we need to report a false answer
        if (ts.calledBy === 'client' && this.animationKeyPressesDetected == 0) {
          this.OnShapeChangeExpected.emit(new ShapeReactionInputEvent(false, this));
        }
        this.ResetFrames(configuration, scene, baseDuration);
      },
    });

    return timeline;
  }

  CreateVisualAnimationPause(
    configuration: ReactionTaskConfiguration,
    scene: Phaser.Scene,
    duration: number
  ): Phaser.Tweens.Timeline {
    let timeline = scene.tweens.createTimeline();
    let follower = { t: 0, vec: new Phaser.Math.Vector2() };

    timeline.add({
      targets: follower,
      t: 1,
      ease: 'Transition',
      duration: duration,
      onComplete: () => {
        //creating animation
        let animation = this.CreateVisualAnimation(configuration, scene);
        this.ReplaceTimeline(ReactionTask.ANIMATION_TIMELINE, animation);
      },
    });

    return timeline;
  }

  SingleAuditoryCase(keyPressesDetected: number) {
    if (keyPressesDetected === 1) {
      this.OnInputReceived.emit(new ReactionInputEvent(true, this));
    } else if (keyPressesDetected > 1) {
      this.OnInputReceived.emit(new ReactionInputEvent(false, this));
    } else {
      this.OnInputReceived.emit(new ReactionInputEvent(false, this));
    }
  }

  DoubleAuditoryCase(keyPressesDetected: number) {
    if (keyPressesDetected === 2) {
      this.OnInputReceived.emit(new ReactionInputEvent(true, this));
    } else if (keyPressesDetected > 2) {
      this.OnInputReceived.emit(new ReactionInputEvent(true, this));
    } else {
      this.OnInputReceived.emit(new ReactionInputEvent(false, this));
    }
  }

  NoResponseCase(keyPressesDetected: number) {
    if (keyPressesDetected > 0) {
      this.RightOrWrongVisualExpected.emit(new ReactionInputEvent(false, this));
    } else if (keyPressesDetected === 0) {
      this.RightOrWrongVisualExpected.emit(new ReactionInputEvent(true, this));
    }
  }

  OneClickCase(keyPressesDetected: number) {
    this.OnInputExpected.emit(new ReactionInputEvent(false, this));

    if (keyPressesDetected === 1) {
      this.RightOrWrongVisualExpected.emit(new ReactionInputEvent(true, this));
    } else {
      this.RightOrWrongVisualExpected.emit(new ReactionInputEvent(false, this));
    }
  }

  TwoClicksCase(keyPressesDetected: number) {
    this.OnInputExpected.emit(new ReactionInputEvent(true, this));

    if (keyPressesDetected === 2) {
      this.RightOrWrongVisualExpected.emit(new ReactionInputEvent(true, this));
    } else {
      this.RightOrWrongVisualExpected.emit(new ReactionInputEvent(false, this));
    }
  }

  NoResponseToVisualCase(configuration: ReactionTaskConfiguration, scene: Phaser.Scene) {
    let { width, height } = this.GetCurrenScreenSize(scene);

    this.image = scene.add
      .sprite(CONSTANTS.reaction.visualReactionSizeX, CONSTANTS.reaction.visualReactionSizeY, 'NoAnswer')
      .setScale(this.CalculateScalingFactor(width, height));

    this.SynchronizeTrackerPosition(scene.sys);
  }

  OneClickToVisualCase(configuration: ReactionTaskConfiguration, scene: Phaser.Scene) {
    this.OnInputExpected.emit(new ReactionInputEvent(false, this));
    let { width, height } = this.GetCurrenScreenSize(scene);

    this.image = scene.add
      .sprite(CONSTANTS.reaction.visualReactionSizeX, CONSTANTS.reaction.visualReactionSizeY, 'OneClick')
      .setScale(this.CalculateScalingFactor(width, height));
    this.SynchronizeTrackerPosition(scene.sys);
  }

  TwoClicksToVisualCase(configuration: ReactionTaskConfiguration, scene: Phaser.Scene) {
    let { width, height } = this.GetCurrenScreenSize(scene);

    this.image = scene.add
      .sprite(CONSTANTS.reaction.visualReactionSizeX, CONSTANTS.reaction.visualReactionSizeY, 'TwoClicks')
      .setScale(this.CalculateScalingFactor(width, height));
    this.SynchronizeTrackerPosition(scene.sys);
  }

  SynchronizeTrackerPosition(sys: Phaser.Scenes.Systems) {
    let position = this.movingTarget.GetTrackerPosition(sys.scene);
    const textureKey = this.getAnimationTextureKey(sys.textures);
    const texture = sys.textures.get(textureKey);
    const sourceImage = texture?.getSourceImage();
    const baseHeight =
      sourceImage &&
      typeof sourceImage === 'object' &&
      'height' in sourceImage &&
      typeof sourceImage.height === 'number'
        ? sourceImage.height
        : 0;
    let newHeight = baseHeight > 0 ? baseHeight : this.movingTarget.image?.displayHeight ?? 0;

    if (this.image) {
      const { width, height } = this.GetCurrenScreenSize(sys.scene);
      const scale = this.CalculateScalingFactor(width, height);
      this.image.setScale(scale);
      newHeight = this.image.displayHeight;
    }

    const themeName = this.movingTarget.configuration.theme?.name;
    const spriteFixPositionY =
      themeName === 'child' && CONSTANTS.reaction.childSpriteFixPositionY !== undefined
        ? CONSTANTS.reaction.childSpriteFixPositionY
        : CONSTANTS.reaction.spriteFixPositionY;

    const correctYPosition = newHeight * spriteFixPositionY;

    this.image?.setPosition(position.x, position.y - correctYPosition);
    this.animation?.setPosition(position.x, position.y);
  }

  CalculateScalingFactor(screenWidth: number, screenHeight: number) {
    const referenceWidth = CONSTANTS.baseScreenWidth;
    const referenceHeight = CONSTANTS.baseScreenHeight;
    let baseScale = CONSTANTS.reaction.spriteScaleSize;

    const themeName = this.movingTarget.configuration.theme?.name;
    if (themeName === 'child') {
      baseScale *= 1.5;
    }

    const widthScale = screenWidth / referenceWidth;
    const heightScale = screenHeight / referenceHeight;

    const averageScale = (widthScale + heightScale) / 2;

    return baseScale * averageScale;
  }

  OnSetVisible(active: boolean): void {
    if (!this.image) {
      return;
    }

    let isOn = active === true && this.configuration.visual === true;

    this.image.setVisible(isOn);
  }
}
